import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import Tip from "@/components/Tip";
import RoadmapDialog from "@/components/RoadmapDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Pill, Mic, Banknote, Stethoscope, FileText, Play, ExternalLink, HeartPulse, Sparkles } from "lucide-react";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDT = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function PatientPortal() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [rx, setRx] = useState([]);
  const [vn, setVn] = useState([]);
  const [inv, setInv] = useState({ entries: [], totals: {} });

  useEffect(() => {
    api.get("/patient/me").then(r => setMe(r.data)).catch(() => setMe({ user, patient_record: null }));
    api.get("/patient/me/prescriptions").then(r => setRx(r.data)).catch(() => setRx([]));
    api.get("/patient/me/voice-notes").then(r => setVn(r.data)).catch(() => setVn([]));
    api.get("/patient/me/invoices").then(r => setInv(r.data)).catch(() => setInv({ entries: [], totals: {} }));
  }, []); // eslint-disable-line

  const patient = me?.patient_record;
  const clinic = me?.clinic;
  const isRecovered = patient?.recovery_status === "recovered";

  const markRecovered = async () => {
    try {
      await api.post("/patient/me/mark-recovered");
      toast.success("Marked as recovered. We'll check in with you soon. 💛");
      const r = await api.get("/patient/me");
      setMe(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark recovered");
    }
  };

  return (
    <div className="min-h-screen app-bg pb-16">
      <RoadmapDialog />
      <AppHeader title="My Health" subtitle={clinic?.name} badge="Patient" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 sm:mt-8 space-y-5 sm:space-y-6">

        {/* Greeting */}
        <section className="glass-panel rounded-2xl p-6 shadow-3d">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-14 w-14 rounded-2xl bg-[#e6f0ff] flex items-center justify-center icon-3d icon-float">
              <Stethoscope className="h-7 w-7 text-[#004a99]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="tiny-label text-[#f37021]">Patient Portal</div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold">
                Hi {user?.name?.split(" ")[0] || "there"} <span className="text-[#004a99]">— your records</span>
              </h1>
              {patient && (
                <p className="text-sm text-slate-500 mt-1">
                  {patient.age ? `${patient.age} years · ` : ""}{patient.phone}
                  {clinic && <> · cared for at <b className="text-slate-700">{clinic.name}</b></>}
                </p>
              )}
              {!patient && (
                <p className="text-sm text-amber-700 mt-1">Your patient record is being linked. Ask the clinic to verify your phone & name.</p>
              )}
            </div>
            {patient && !isRecovered && (
              <Button onClick={markRecovered} className="bg-emerald-600 hover:bg-emerald-700 text-white btn-sheen" data-testid="patient-mark-recovered">
                <HeartPulse className="h-4 w-4 mr-1.5 icon-wiggle" /> I'm feeling better
                <Tip text="Tap when you've recovered. Two days later we'll check in and ask if there's anyone else we can help — totally optional." />
              </Button>
            )}
            {isRecovered && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border-2 border-emerald-200 text-emerald-800 font-semibold" data-testid="patient-recovered-badge">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                Recovered · we'll check in soon 💛
              </div>
            )}
          </div>
        </section>

        {/* KPIs strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="solid-panel p-4 shadow-3d">
            <div className="tiny-label">Prescriptions</div>
            <div className="text-2xl font-heading font-bold mt-1" data-testid="patient-rx-count">{rx.length}</div>
            <div className="text-xs text-slate-500">in your history</div>
          </div>
          <div className="solid-panel p-4 shadow-3d">
            <div className="tiny-label">Voice notes</div>
            <div className="text-2xl font-heading font-bold mt-1" data-testid="patient-vn-count">{vn.length}</div>
            <div className="text-xs text-slate-500">from your doctor</div>
          </div>
          <div className="solid-panel p-4 shadow-3d">
            <div className="tiny-label">Paid</div>
            <div className="text-2xl font-heading font-bold text-emerald-700 mt-1">{fmtINR(inv.totals?.paid_amount)}</div>
            <div className="text-xs text-slate-500">{inv.entries.filter(e => e.status === "paid").length} invoices</div>
          </div>
          <div className="solid-panel p-4 shadow-3d">
            <div className="tiny-label">Pending</div>
            <div className="text-2xl font-heading font-bold text-amber-700 mt-1">{fmtINR(inv.totals?.pending_amount)}</div>
            <div className="text-xs text-slate-500">{inv.entries.filter(e => e.status !== "paid").length} invoices</div>
          </div>
        </section>

        {/* Tabs */}
        <Tabs defaultValue="prescriptions">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
            <TabsTrigger value="prescriptions" data-testid="patient-tab-rx"><Pill className="h-4 w-4 mr-1.5" /> Prescriptions</TabsTrigger>
            <TabsTrigger value="voice-notes" data-testid="patient-tab-vn"><Mic className="h-4 w-4 mr-1.5" /> Voice notes</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="patient-tab-invoices"><Banknote className="h-4 w-4 mr-1.5" /> Invoices</TabsTrigger>
          </TabsList>

          <TabsContent value="prescriptions" className="mt-4">
            <section className="solid-panel p-6 shadow-3d">
              <h2 className="text-lg font-heading font-bold flex items-center mb-3">
                Prescriptions <Tip text="Read-only history of every prescription from your doctor. Tap PDF to download." />
              </h2>
              {rx.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No prescriptions yet.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {rx.map((n) => (
                    <div key={n.id} className="py-4 flex items-start gap-3" data-testid={`patient-rx-${n.id}`}>
                      <FileText className="h-5 w-5 text-[#f37021] mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-slate-900">{n.structured?.chief_complaint || "Consultation"}</div>
                          <span className="text-xs text-slate-500">{fmtDT(n.created_at)}</span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">By <b>{n.doctor_name || "Doctor"}</b></div>
                        {Array.isArray(n.structured?.prescription) && n.structured.prescription.length > 0 && (
                          <ul className="mt-2 text-sm text-slate-700 space-y-0.5">
                            {n.structured.prescription.slice(0, 4).map((rx, i) => (
                              <li key={i}>💊 <b>{rx.drug}</b> {rx.dose} · {rx.frequency} · {rx.duration}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {n.pdf_url && (
                        <Button variant="outline" size="sm" asChild data-testid={`patient-rx-pdf-${n.id}`}>
                          <a href={n.pdf_url} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> PDF</a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="voice-notes" className="mt-4">
            <section className="solid-panel p-6 shadow-3d">
              <h2 className="text-lg font-heading font-bold flex items-center mb-3">
                Voice notes from your doctor <Tip text="Play to listen. Each note is encrypted and shared only with you." />
              </h2>
              {vn.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No voice notes yet.</div>
              ) : (
                <div className="space-y-3">
                  {vn.map((v) => (
                    <div key={v.id} className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`patient-vn-${v.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold text-slate-900 flex items-center"><Play className="h-4 w-4 mr-1 text-[#f37021]" /> {v.doctor_name || "Doctor"}</div>
                          <div className="text-xs text-slate-500">{fmtDT(v.created_at)} · {v.detected_language?.toUpperCase()}</div>
                        </div>
                      </div>
                      <audio controls src={v.audio_url} className="w-full" data-testid={`patient-vn-audio-${v.id}`} />
                      {v.transcript && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{v.transcript}</p>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <section className="solid-panel p-6 shadow-3d">
              <h2 className="text-lg font-heading font-bold flex items-center mb-3">
                Your invoices <Tip text="Paid invoices are settled. Pending invoices are gentle reminders only — no late fees." />
              </h2>
              {inv.entries.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No invoices on file.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Date</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {inv.entries.map((e) => (
                      <tr key={e.id} className="border-b last:border-b-0" data-testid={`patient-inv-${e.id}`}>
                        <td className="py-2">{e.due_at?.slice(0, 10)}</td>
                        <td className="font-semibold">{fmtINR(e.amount)}</td>
                        <td>
                          {e.status === "paid"
                            ? <span className="text-emerald-700 font-semibold">✓ Paid</span>
                            : <span className="text-amber-700 font-semibold">Pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
